import React, { useEffect, useState } from 'react';
import { Paper } from '../../pages/UnpublishedPapers';

interface AddPaperModalProps {
  visible: boolean;
  initialValues?: Paper | null;
  onClose: () => void;
  onPaperAdded: () => void;
}

const AddPaperModal: React.FC<AddPaperModalProps> = ({
  visible = false,
  initialValues,
  onClose,
  onPaperAdded,
}) => {
  const [loading, setLoading] = useState(false);
  const [formValues, setFormValues] = useState({
    title: initialValues?.title || '',
    abstract: initialValues?.abstract || '',
    authors: initialValues?.authors || [],
    published: false,
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const isEdit = !!initialValues;

  useEffect(() => {
    if (initialValues) {
      setFormValues({
        title: initialValues?.title || '',
        abstract: initialValues?.abstract || '',
        authors: initialValues?.authors || '',
        published: false,
      });
    } else {
      setFormValues({
        title: '',
        abstract: '',
        authors: [],
        published: false,
      });
    }
  }, [initialValues]);

  const handleSubmit = async () => {
    setLoading(true);
    const payload = formValues;
    try {
      const response = await fetch(
        `https://academichub-backend.onrender.com/unpublishedpapers/${
          isEdit ? `update/${initialValues.paperId}` : 'add'
        }`,
        {
          method: isEdit ? 'PATCH' : 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add student');
      }

      setFormValues({
        title: '',
        abstract: '',
        authors: [],
        published: false,
      });
      onClose();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }

    onPaperAdded();
  };

  const handleAuthorChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: 'id' | 'name'
  ) => {
    const updatedAuthors = [...formValues.authors];
    updatedAuthors[index] = {
      ...updatedAuthors[index],
      [field]: e.target.value,
    };
    setFormValues({ ...formValues, authors: updatedAuthors });
  };

  const addAuthor = () => {
    setFormValues({
      ...formValues,
      authors: [...formValues.authors, { id: '', name: '' }],
    });
  };

  const removeAuthor = (index: number) => {
    const updatedAuthors = formValues.authors.filter((_, i: number) => i !== index);
    setFormValues({ ...formValues, authors: updatedAuthors });
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ${
        visible ? '' : 'hidden'
      }`}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isEdit ? 'Edit' : 'Add'} Unpublished Paper</h2>
          <button onClick={onClose}>
            <i className="fa fa-x text-xs" />
          </button>
        </div>
        <form className="overflow-auto max-h-[75vh]">
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Title</label>
            <input
              type="text"
              name="title"
              value={formValues.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1 text-sm">Abstract</label>
            <input
              type="text"
              name="abstract"
              value={formValues.abstract}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border rounded-md text-sm font-normal"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Authors</label>
            {formValues.authors?.map((author: { id: string; name: string }, index: number) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={author.name}
                  onChange={e => handleAuthorChange(e, index, 'name')}
                  className="w-full px-3 py-2 border rounded-md text-sm font-normal"
                  placeholder={`Author ${index + 1} Name`}
                />
                <input
                  type="text"
                  value={author.id}
                  onChange={e => handleAuthorChange(e, index, 'id')}
                  className="w-1/3 px-3 py-2 border rounded-md text-sm font-normal"
                  placeholder={`Author ${index + 1} ID`}
                />
                <button
                  type="button"
                  onClick={() => removeAuthor(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addAuthor}
              className="text-blue-500 hover:text-blue-700 text-sm mt-2"
            >
              Add another author
            </button>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-2 px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className={`text-sm px-3 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={loading}
            >
              {loading ? 'Adding...' : `${isEdit ? 'Edit' : 'Add'} Paper`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaperModal;
